import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, CheckCircle2, ClipboardList, Loader2, Store, X, Paperclip, FileCheck2,
} from 'lucide-react';
import EscolherAnexo from '../../EscolherAnexo';
import { abrirArquivoComToken } from '../../../utils/arquivoComToken';
import { derivados, normalizarValor } from './mapa-caixa-derivados';

// =====================================================================
// FINANCEIRO — Mapa de Caixa: o FORMULÁRIO da loja (um dia).
//
// A loja escolhe a data, anexa o scan do "Controle Diário" e transcreve os
// totais em três blocos (Dinheiro e Sangria · GPRS · Erros e Nulos). A Quebra de
// Caixa e a Diferença GPRS são CALCULADAS ao vivo — a loja não digita conta.
//
// ⚠️ É UPSERT por loja/dia: ao escolher um dia já lançado, o formulário carrega o
//    que existe (para corrigir). O anexo é OBRIGATÓRIO num dia novo e OPCIONAL na
//    edição (quem só conserta um número não re-envia o scan).
// ⚠️ A tela NÃO reimplementa regra de perfil nem faz conta de dinheiro: quem diz
//    se registra/escolhe loja é o `GET /opcoes`, e as contas vêm do espelho
//    `mapa-caixa-derivados.js` (travado contra o servidor por teste).
// =====================================================================

const API = '/api/financeiro/mapa-caixa';
const COR = '#0d7a74';
const COR_FORTE = '#115e59';
const COR_FUNDO = '#f0fdfa';
const COR_BORDA = '#99f6e4';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const brl = (n) => Number(n || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (iso) => (iso ? String(iso).split('-').reverse().join('/') : '—');
const usuarioLogado = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};

// Número do banco → texto do campo (PT-BR). '' para vazio; ponto vira vírgula.
const paraCampo = (n) => (n === null || n === undefined ? '' : String(n).replace('.', ','));

const CAMPOS_VAZIOS = {
  totalDinheiroFita: '', totalValorBananas: '', qtdBananas: '', pettyCashSacado: '',
  gprsConsolidado: '', gprsMaquina: '', gprsFlex: '',
  errosPosTotal: '', nulosPosTotal: '', pctReferenteVenda: '',
};

const Campo = ({ label, obrigatorio, dica, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <label style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>
      {label} {obrigatorio && <span style={{ color: '#dc2626' }}>*</span>}
    </label>
    {children}
    {dica && <span style={{ fontSize: 12, color: '#94a3b8' }}>{dica}</span>}
  </div>
);

const estiloInput = {
  padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 15, color: '#0f172a', background: '#fff', width: '100%',
  boxSizing: 'border-box', fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums',
};

// Cabeçalho de bloco (Dinheiro e Sangria / GPRS / Erros e Nulos).
const Bloco = ({ titulo, children }) => (
  <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 18 }}>
    <h4 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 800, color: COR_FORTE, textTransform: 'uppercase', letterSpacing: 0.4 }}>{titulo}</h4>
    {children}
  </div>
);

// A "caixinha" de um valor DERIVADO (não editável) — Quebra / Diferença GPRS.
const Derivado = ({ label, valor }) => {
  const vazio = valor === null || valor === undefined;
  const cor = vazio ? '#94a3b8' : (valor > 0 ? '#047857' : valor < 0 ? '#b91c1c' : '#334155');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{label}</label>
      <div style={{
        ...estiloInput, background: '#f8fafc', fontWeight: 800, color: cor,
        display: 'flex', alignItems: 'center',
      }}>
        {vazio ? '—' : `${valor > 0 ? '+' : ''}${brl(valor)}`}
      </div>
      <span style={{ fontSize: 12, color: '#94a3b8' }}>Calculado — não digite.</span>
    </div>
  );
};

const gridCampos = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 };

const MapaCaixaRegistro = ({ onIrParaConsulta }) => {
  const [opcoes, setOpcoes] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [gravado, setGravado] = useState(null);

  const [form, setForm] = useState({ data: '', loja: '', ...CAMPOS_VAZIOS });
  const [anexo, setAnexo] = useState(null);
  // O dia já existe no banco? Guarda o anexo atual para a edição (anexo opcional).
  const [existente, setExistente] = useState(null); // { anexoUrl, registradoPor, atualizadoPor } | null

  // Correções de data que o escritório fez em mapas desta loja e que ninguém da
  // loja confirmou ter visto (a faixa no topo). Falha de leitura = sem faixa.
  const [avisos, setAvisos] = useState([]);
  const [confirmando, setConfirmando] = useState(false);

  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  // --- carrega opções + lojas ---------------------------------------
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const rOpcoes = await fetch(`${API}/opcoes`, { headers: auth() });
        if (!rOpcoes.ok) throw new Error('opcoes');
        const o = await rOpcoes.json();
        if (!vivo) return;
        setOpcoes(o);
        if (o.escolheLoja) {
          const rLojas = await fetch(`${API}/lojas`, { headers: auth() });
          if (vivo && rLojas.ok) setLojas(await rLojas.json());
        }
        setForm((f) => ({
          ...f,
          // ⚠️ DATA VAZIA DE PROPÓSITO (nov/2026): vinha pré-preenchida com HOJE, e a
          //    loja, que lança o mapa do dia fechado no dia seguinte, deixava assim —
          //    o mapa caía no dia errado e a conciliação do Financeiro quebrava. Campo
          //    vazio + `required` obriga a pessoa a informar o dia do fechamento.
          data: '',
          loja: o.escolheLoja ? '' : String(usuarioLogado()?.loja || '').trim(),
        }));
      } catch {
        if (vivo) setErro('Não foi possível carregar o formulário. Recarregue a página.');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await fetch(`${API}/avisos`, { headers: auth() });
        if (vivo && r.ok) setAvisos(await r.json());
      } catch { /* sem faixa: o lançamento segue normal */ }
    })();
    return () => { vivo = false; };
  }, []);

  const confirmarCiencia = async () => {
    setConfirmando(true);
    try {
      const r = await fetch(`${API}/avisos/ciente`, { method: 'POST', headers: auth() });
      if (r.ok) setAvisos([]);
    } finally {
      setConfirmando(false);
    }
  };

  // --- ao mudar loja/dia, carrega o que já existe (para corrigir) ----
  // ⚠️ Deps SÓ [loja, data]: os campos de valor NÃO entram aqui, senão cada
  //    tecla do usuário dispararia um recarregamento que apagaria o que ele
  //    acabou de digitar.
  useEffect(() => {
    if (!opcoes) return;
    const data = form.data;
    const loja = form.loja;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(data)) return;
    if (opcoes.escolheLoja && !loja) return;

    let vivo = true;
    (async () => {
      try {
        const params = new URLSearchParams({ data });
        if (opcoes.escolheLoja) params.set('loja', loja);
        const r = await fetch(`${API}/dia?${params.toString()}`, { headers: auth() });
        if (!vivo || !r.ok) return;
        const d = await r.json();
        if (!vivo) return;
        if (d.existe) {
          setExistente({ anexoUrl: d.anexoUrl, registradoPor: d.registradoPor, atualizadoPor: d.atualizadoPor });
          setForm((f) => ({
            ...f,
            totalDinheiroFita: paraCampo(d.totalDinheiroFita),
            totalValorBananas: paraCampo(d.totalValorBananas),
            qtdBananas: d.qtdBananas === null || d.qtdBananas === undefined ? '' : String(d.qtdBananas),
            pettyCashSacado: paraCampo(d.pettyCashSacado),
            gprsConsolidado: paraCampo(d.gprsConsolidado),
            gprsMaquina: paraCampo(d.gprsMaquina),
            gprsFlex: paraCampo(d.gprsFlex),
            errosPosTotal: paraCampo(d.errosPosTotal),
            nulosPosTotal: paraCampo(d.nulosPosTotal),
            pctReferenteVenda: paraCampo(d.pctReferenteVenda),
          }));
        } else {
          // Dia novo: limpa os valores (preserva loja e data) e zera o "existente".
          setExistente(null);
          setForm((f) => ({ ...f, ...CAMPOS_VAZIOS }));
        }
        setAnexo(null);
      } catch { /* silencioso: um dia que não carrega ainda pode ser lançado do zero */ }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.loja, form.data, opcoes]);

  const enviar = async (e) => {
    e.preventDefault();
    setErro('');
    if (!existente && !anexo) {
      setErro('Anexe o scan do Controle Diário — o anexo é obrigatório para um dia novo.');
      return;
    }
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('data', form.data);
      if (opcoes?.escolheLoja) fd.append('loja', form.loja);
      for (const campo of Object.keys(CAMPOS_VAZIOS)) fd.append(campo, form[campo]);
      if (anexo) fd.append('anexo', anexo);

      const r = await fetch(API, { method: 'POST', headers: auth(), body: fd });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível salvar o mapa de caixa.');
      setGravado(corpo);
    } catch (ex) {
      setErro(ex.message);
    } finally {
      setEnviando(false);
    }
  };

  const novoRegistro = () => {
    setGravado(null);
    setAnexo(null);
    setExistente(null);
    // A data também volta vazia: o próximo mapa é de OUTRO dia, e herdar a anterior
    // faria a pessoa lançar por cima sem perceber.
    setForm((f) => ({ ...f, ...CAMPOS_VAZIOS, data: '' }));
  };

  if (carregando) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', padding: 24 }}>
        <Loader2 size={18} className="spin" /> Carregando o formulário…
      </div>
    );
  }

  if (!opcoes?.podeLancar) {
    return (
      <div style={{ padding: 24, background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
        Seu perfil consulta o Mapa de Caixa, mas não lança mapas.
      </div>
    );
  }

  // --- confirmação ---------------------------------------------------
  if (gravado) {
    const linha = (rotulo, valor) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
        <span style={{ color: '#64748b', fontSize: 14 }}>{rotulo}</span>
        <span style={{ color: '#0f172a', fontSize: 14, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{valor}</span>
      </div>
    );
    const q = gravado.quebraCaixa;
    const dg = gravado.diferencaGprs;
    return (
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'inline-flex', padding: 16, background: '#dcfce7', borderRadius: '50%', marginBottom: 12 }}>
            <CheckCircle2 size={36} color="#16a34a" />
          </div>
          <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
            Mapa {gravado.editado ? 'atualizado' : 'lançado'}
          </h3>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14 }}>
            {gravado.loja} — {(gravado.data || '').split('-').reverse().join('/')}
          </p>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          {linha('Total Dinheiro Fita', brl(gravado.totalDinheiroFita))}
          {linha('Total Depositado (bananas)', brl(gravado.totalValorBananas))}
          {linha('Quebra de Caixa', q === null || q === undefined ? '—' : `${q > 0 ? '+' : ''}${brl(q)}`)}
          {linha('Diferença GPRS', dg === null || dg === undefined ? '—' : `${dg > 0 ? '+' : ''}${brl(dg)}`)}
          {linha('Anexo', gravado.anexoUrl ? 'Anexado' : '—')}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button onClick={novoRegistro} style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px',
            background: COR, color: '#fff', border: 'none', borderRadius: 8,
            fontWeight: 700, fontSize: 15, cursor: 'pointer',
          }}>
            <ClipboardList size={18} /> Lançar outro dia
          </button>
          {onIrParaConsulta && (
            <button onClick={onIrParaConsulta} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 20px',
              background: COR_FUNDO, color: COR_FORTE, border: `1px solid ${COR_BORDA}`,
              borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer',
            }}>
              Ver a consulta
            </button>
          )}
        </div>
      </div>
    );
  }

  // --- formulário ----------------------------------------------------
  const der = derivados({
    totalValorBananas: normalizarValor(form.totalValorBananas),
    totalDinheiroFita: normalizarValor(form.totalDinheiroFita),
    gprsMaquina: normalizarValor(form.gprsMaquina),
    gprsConsolidado: normalizarValor(form.gprsConsolidado),
  });

  const campoValor = (campo, label, extra) => (
    <Campo label={label} dica={extra}>
      <input value={form[campo]} onChange={(e) => set(campo, e.target.value)}
        placeholder="0,00" inputMode="decimal" style={estiloInput} />
    </Campo>
  );

  return (
    <form onSubmit={enviar} style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Mapa de Caixa</h3>
        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>
          Anexe o scan do Controle Diário e transcreva os totais. A Quebra de Caixa e a
          Diferença GPRS são calculadas automaticamente. Um dia já lançado é carregado para correção.
        </p>
      </div>

      {avisos.length > 0 && (
        <div style={{
          padding: 14, marginBottom: 20, background: '#fffbeb', color: '#92400e',
          border: '1px solid #fcd34d', borderRadius: 8, fontSize: 14, lineHeight: 1.5,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <strong>O Financeiro corrigiu a data de {avisos.length === 1 ? 'um mapa' : `${avisos.length} mapas`} da sua loja.</strong>
              <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
                {avisos.map((a) => (
                  <li key={`${a.loja}-${a.data}`}>
                    Lançado em <strong>{dataBr(a.dataOriginal)}</strong>, o correto era <strong>{dataBr(a.data)}</strong>
                    {a.corrigidoPor ? ` (corrigido por ${a.corrigidoPor})` : ''}.
                  </li>
                ))}
              </ul>
              O mapa deve ser lançado <strong>na data do dia fechado</strong>, não na data em que você digita:
              a conciliação diária das vendas depende disso.
            </div>
          </div>
          <button type="button" onClick={confirmarCiencia} disabled={confirmando} style={{
            marginTop: 10, padding: '8px 16px', background: confirmando ? '#cbd5e1' : '#b45309', color: '#fff',
            border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: confirmando ? 'default' : 'pointer',
          }}>
            {confirmando ? 'Confirmando…' : 'Estou ciente'}
          </button>
        </div>
      )}

      {erro && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: 14, marginBottom: 20,
          background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 14,
        }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} /> <span>{erro}</span>
        </div>
      )}

      {existente && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: 12, marginBottom: 18,
          background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13,
        }}>
          <FileCheck2 size={18} style={{ flexShrink: 0 }} />
          <span>Este dia já foi lançado — você está <strong>corrigindo</strong>. O anexo só muda se você enviar um novo.</span>
        </div>
      )}

      {/* Loja + Data */}
      <div style={{ ...gridCampos, marginBottom: 18 }}>
        <Campo label="Loja" obrigatorio>
          {opcoes.escolheLoja ? (
            <select required value={form.loja} onChange={(e) => set('loja', e.target.value)} style={estiloInput}>
              <option value="">Escolha a loja…</option>
              {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          ) : (
            <div style={{ ...estiloInput, display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', color: '#334155', fontWeight: 700 }}>
              <Store size={16} color={COR} /> {form.loja || '—'}
            </div>
          )}
        </Campo>
        <Campo label="Data do movimento" obrigatorio dica="O dia em que o caixa foi fechado (o dia do Controle Diário), não o dia de hoje.">
          <input type="date" required value={form.data} max={opcoes.hoje}
            onChange={(e) => set('data', e.target.value)} style={estiloInput} />
        </Campo>
      </div>

      {/* Bloco 1 — Dinheiro e Sangria */}
      <Bloco titulo="Dinheiro e Sangria">
        <div style={gridCampos}>
          {campoValor('totalDinheiroFita', 'Total Dinheiro Fita')}
          {campoValor('totalValorBananas', 'Total Valor Bananas', 'Total depositado da sangria.')}
          <Campo label="Total Nº Bananas" dica="Nº de boletas depositadas.">
            <input value={form.qtdBananas} onChange={(e) => set('qtdBananas', e.target.value)}
              placeholder="0" inputMode="numeric" style={estiloInput} />
          </Campo>
          {campoValor('pettyCashSacado', 'Petty Cash Sacado')}
          <Derivado label="Quebra de Caixa" valor={der.quebraCaixa} />
        </div>
      </Bloco>

      {/* Bloco 2 — GPRS */}
      <Bloco titulo="GPRS">
        <div style={gridCampos}>
          {campoValor('gprsConsolidado', 'Total Fita GPRS Consolidado')}
          {campoValor('gprsMaquina', 'Total Fita GPRS Máquina')}
          {campoValor('gprsFlex', 'Total GPRS FLEX')}
          <Derivado label="Diferença" valor={der.diferencaGprs} />
        </div>
      </Bloco>

      {/* Bloco 3 — Erros e Nulos */}
      <Bloco titulo="Erros e Nulos">
        <div style={gridCampos}>
          {campoValor('errosPosTotal', 'Erros Pós Total')}
          {campoValor('nulosPosTotal', 'Nulos Pós Total')}
          <Campo label="% Referente a Venda" dica="Percentual (0–100).">
            <input value={form.pctReferenteVenda} onChange={(e) => set('pctReferenteVenda', e.target.value)}
              placeholder="0,00" inputMode="decimal" style={estiloInput} />
          </Campo>
        </div>
      </Bloco>

      {/* Anexo */}
      <div style={{ marginBottom: 24 }}>
        <Campo label="Anexo do Mapa de Caixa" obrigatorio={!existente}
          dica="Foto OU PDF do formulário de papel. Máximo 12 MB.">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <EscolherAnexo
              cor={COR}
              nome={anexo?.name || ''}
              rotulo="Anexar pdf"
              onArquivo={(arq) => setAnexo(arq || null)}
            />
            {anexo && (
              <button type="button" onClick={() => setAnexo(null)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6,
                fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}>
                <X size={14} /> Remover
              </button>
            )}
            {!anexo && existente?.anexoUrl && (
              <button type="button"
                onClick={() => abrirArquivoComToken(`${API}/anexo?url=${encodeURIComponent(existente.anexoUrl)}`, 'mapa-de-caixa')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                  background: COR_FUNDO, color: COR_FORTE, border: `1px solid ${COR_BORDA}`,
                  borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}>
                <Paperclip size={14} /> Ver anexo atual
              </button>
            )}
          </div>
        </Campo>
      </div>

      <button type="submit" disabled={enviando} style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        width: '100%', padding: '14px 24px', background: enviando ? '#cbd5e1' : COR,
        color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 16,
        cursor: enviando ? 'default' : 'pointer',
      }}>
        {enviando ? <><Loader2 size={18} className="spin" /> Salvando…</> : <>{existente ? 'Salvar correção' : 'Lançar mapa'}</>}
      </button>
    </form>
  );
};

export default MapaCaixaRegistro;
